import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Chip,
  CircularProgress,
  Link,
  IconButton,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import UserFeedbackService from '../../../Services/userFeedback.service';
import {
  markFeedbackSubmitted,
  markFeedbackSnoozed,
  markFeedbackDontAsk,
} from '../../../Utils/userFeedbackTiming';

const CATEGORIES = ['Bug', 'Suggestion', 'Compliment', 'Other'];

export default function UserFeedbackModal({
  open,
  onClose,
  token,
  userId,
  source = 'Web',
  isAuto = false,
}) {
  const { t, i18n } = useTranslation();
  const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [category, setCategory] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const reset = () => {
    setRating(0);
    setHover(0);
    setCategory(null);
    setComment('');
    setError('');
    setSubmitting(false);
    setDone(false);
  };

  const handleClose = (action) => {
    if (action === 'snooze' && isAuto) {
      markFeedbackSnoozed(userId);
    }
    reset();
    onClose?.(action);
  };

  const handleDontAsk = () => {
    markFeedbackDontAsk(userId);
    reset();
    onClose?.('dontAsk');
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      setError(t('FeedbackRatingRequired') || 'Please select a rating');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await UserFeedbackService.create(token, {
        rating,
        comment: comment.trim() || null,
        category,
        source,
      });
      markFeedbackSubmitted(userId);
      setDone(true);
      setTimeout(() => {
        reset();
        onClose?.('submitted');
      }, 1200);
    } catch (e) {
      setError(e.message || t('FeedbackSubmitError') || 'Could not submit');
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(isAuto ? 'snooze' : 'close')}
      maxWidth="sm"
      fullWidth
      dir={direction}
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          fontFamily: 'Montserrat',
          fontWeight: 700,
          position: 'relative',
          paddingInlineEnd: 6,
        }}
      >
        {t('FeedbackTitle') || 'How is Xtram working for you?'}
        <IconButton
          aria-label="close"
          onClick={() => handleClose(isAuto ? 'snooze' : 'close')}
          sx={{
            position: 'absolute',
            insetInlineEnd: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {done ? (
          <Typography align="center" sx={{ py: 3, fontFamily: 'Montserrat' }}>
            {t('FeedbackThanks') || 'Thank you for your feedback!'}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Montserrat' }}>
              {t('FeedbackSubtitle') || 'Your opinion helps us improve the product.'}
            </Typography>
            <Box display="flex" justifyContent="center" gap={0.5} mb={2}>
              {[1, 2, 3, 4, 5].map((value) => {
                const filled = (hover || rating) >= value;
                return (
                  <IconButton
                    key={value}
                    onMouseEnter={() => setHover(value)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(value)}
                    sx={{ color: filled ? '#f5a623' : '#ccc', p: 0.5 }}
                  >
                    {filled ? <StarIcon fontSize="large" /> : <StarBorderIcon fontSize="large" />}
                  </IconButton>
                );
              })}
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center" mb={2}>
              {CATEGORIES.map((c) => (
                <Chip
                  key={c}
                  label={t(`FeedbackCategory_${c}`) || c}
                  clickable
                  color={category === c ? 'primary' : 'default'}
                  variant={category === c ? 'filled' : 'outlined'}
                  onClick={() => setCategory(category === c ? null : c)}
                  sx={{ fontFamily: 'Montserrat' }}
                />
              ))}
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('FeedbackCommentPlaceholder') || 'Tell us what we can improve (optional)'}
              inputProps={{ maxLength: 2000 }}
              sx={{ fontFamily: 'Montserrat' }}
            />
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      {!done && (
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={submitting}
            onClick={handleSubmit}
            sx={{ borderRadius: 2, py: 1.2, fontFamily: 'Montserrat', fontWeight: 600 }}
          >
            {submitting ? <CircularProgress size={22} color="inherit" /> : (t('FeedbackSubmit') || 'Submit')}
          </Button>
          <Button
            fullWidth
            variant="text"
            disabled={submitting}
            onClick={() => handleClose(isAuto ? 'snooze' : 'close')}
            sx={{ fontFamily: 'Montserrat' }}
          >
            {t('FeedbackMaybeLater') || 'Maybe later'}
          </Button>
          {isAuto && (
            <Link
              component="button"
              underline="hover"
              onClick={handleDontAsk}
              sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'Montserrat' }}
            >
              {t('FeedbackDontAskAgain') || "Don't ask again"}
            </Link>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
